import { useState, useEffect, useMemo } from "react";
import { FaCheck, FaEdit } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import ExportOptions from "../../../ExportOptions";
import ConfirmActionModal from "../../../ConfirmDeleteModal";
import TableSkeleton from "../../../TableSkeleton";
import HospitalAgentModal from "./HospitalAgentModal";
import PageHeading from "../../../PageHeading";

const HospitalAgentList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [agentToEdit, setAgentToEdit] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const itemsPerPage = 10;

  // Fetch agents from API
  const fetchAgents = async () => {
    try {
      const response = await axios.get(
        "https://api.muktihospital.com/api/hospital/agent",
        {
          headers: {
            "x-api-key":
              "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
          },
        }
      );
      // console.log("Raw API response:", response.data); // Debug the response structure
      const data = Array.isArray(response.data.agents)
        ? response.data.agents
        : [];
      if (!Array.isArray(data)) {
        console.error("Expected an array, got:", data);
        throw new Error("Invalid API response: agents is not an array");
      }
      const sanitizedAgents = data.map((agent) => ({
        id: agent.id || Math.random().toString(36).substr(2, 9),
        agentCode: agent.agentCode || "",
        name: agent.agentName || "",
        contactNo: agent.contactNumber || "",
        address: agent.address || "",
        division: agent.division || "Dhaka",
        district: agent.district || "Dhaka",
        upazila: agent.upazila || "",
        union: agent.union || "",
      }));
      // console.log("Sanitized agents:", sanitizedAgents);
      setAgents(sanitizedAgents);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching agents:", error);
      toast.error("Failed to load agents.", {
        position: "top-right",
        autoClose: 3000,
      });
      setAgents([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, [refreshTrigger]);

  // Debug agents state changes
  useEffect(() => {
    // console.log("Agents state updated:", agents);
  }, [agents]);

  // Defensive filter to prevent TypeError
  const filteredAgents = agents.filter(
    (agent) =>
      (agent.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (agent.agentCode || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (agent.contactNo || "").toLowerCase().includes(searchQuery.toLowerCase()) // Added this line for phone number search
  );

  const totalPages = Math.ceil(filteredAgents.length / itemsPerPage);

  const currentData = filteredAgents
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    .map((agent, index) => ({
      ...agent,
      displayId: (currentPage - 1) * itemsPerPage + index + 1,
    }));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(currentData.map((agent) => agent.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (agentId) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(agentId)
        ? prevSelectedRows.filter((id) => id !== agentId)
        : [...prevSelectedRows, agentId]
    );
  };

  const handleDelete = async (agentIds) => {
    try {
      const deletePromises = agentIds.map(async (id) => {
        try {
          await axios.delete(
            `https://api.muktihospital.com/api/hospital/agent/delete/${id}`,
            {
              headers: {
                "x-api-key":
                  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
              },
            }
          );
          return { id, success: true };
        } catch (error) {
          console.error(
            `Error deleting agent ${id}:`,
            error.response?.data || error.message
          );
          return {
            id,
            success: false,
            error: error.response?.data?.error || "Failed to delete agent",
          };
        }
      });

      const results = await Promise.all(deletePromises);
      const successfulDeletions = results
        .filter((result) => result.success)
        .map((result) => result.id);
      const failedDeletions = results.filter((result) => !result.success);

      if (successfulDeletions.length > 0) {
        setAgents((prevAgents) =>
          prevAgents.filter((agent) => !successfulDeletions.includes(agent.id))
        );
        setSelectedRows((prevSelectedRows) =>
          prevSelectedRows.filter((id) => !successfulDeletions.includes(id))
        );
        toast.success(
          `${successfulDeletions.length} agent(s) deleted successfully!`,
          {
            position: "top-right",
            autoClose: 3000,
          }
        );
      }

      if (failedDeletions.length > 0) {
        failedDeletions.forEach((result) => {
          toast.error(`Failed to delete agent ${result.id}: ${result.error}`, {
            position: "top-right",
            autoClose: 5000,
          });
        });
      }

      // Trigger re-fetch to ensure UI consistency
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Unexpected error during deletion:", error);
      toast.error("An unexpected error occurred while deleting agent(s).", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleEdit = (agent) => {
    setAgentToEdit({
      id: agent.id,
      agentCode: agent.agentCode,
      name: agent.name,
      contactNo: agent.contactNo,
      address: agent.address,
      division: agent.division,
      district: agent.district,
      upazila: agent.upazila,
      union: agent.union,
    });
    setAgentModalOpen(true);
  };

  const handleSaveAgent = (savedAgent, isEditMode) => {
    // console.log("Received saved agent:", savedAgent);
    if (isEditMode) {
      setAgents((prevAgents) =>
        prevAgents.map((agent) =>
          agent.id === savedAgent.id ? savedAgent : agent
        )
      );
      // Trigger re-fetch to ensure data consistency
      setRefreshTrigger((prev) => prev + 1);
    } else {
      setAgents((prevAgents) => [savedAgent, ...prevAgents]);
    }
    toast.success(isEditMode ? "Agent updated successfully!" : "Agent added successfully!", {
      position: "top-right",
      autoClose: 3000,
    });
    setAgentModalOpen(false);
    setAgentToEdit(null);
  };

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const headers = ["Serial No", "Agent Code", "Agent Name", "Contact Number"];
  const dataMapper = (item) =>
    [item.displayId, item.agentCode, item.name, item.contactNo].join("\t");

  const skeletonColumns = [
    {
      width: "40px",
      height: "16px",
      className: "text-left print-hidden-in-print",
      skeletonClassName: "my-5 mx-3",
    },
    {
      width: "80px",
      height: "16px",
      className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "100px",
      height: "16px",
      className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "120px",
      height: "16px",
      className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "80px",
      height: "16px",
      className: "text-left p-3 print-hidden-in-print",
      skeletonClassName: "ml-auto",
    },
    {
      width: "80px",
      height: "16px",
      className: "text-right p-3 print-hidden-in-print",
      skeletonClassName: "ml-auto",
    },
  ];
  
  return (
    <div>
      <ToastContainer />
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Agent List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="sm:max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by agent name, code, or phone" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="flex items-center w-full justify-between gap-3">
              <button
                onClick={() => {
                  setAgentToEdit(null);
                  setAgentModalOpen(true);
                }}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
              >
                + Add Agent
              </button>
              <ExportOptions
                data={currentData}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                title="Agent List"
                headers={headers}
                dataMapper={dataMapper}
              />
            </div>
          </div>
        </div>
        <div>
          <div className="overflow-x-auto print-wrapper">
            {loading ? (
              <div className="print-hidden">
                <table className="min-w-[600px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]"></div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Serial No
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Agent Code
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Agent Name
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Contact Number
                      </th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <TableSkeleton
                      rowCount={itemsPerPage}
                      columns={skeletonColumns}
                      baseColor="#e5e7eb"
                      highlightColor="#f3f4f6"
                    />
                  </tbody>
                </table>
              </div>
            ) : filteredAgents.length === 0 ? (
              <div className="p-5 text-center text-gray-600 print-hidden">
                No agents found.
              </div>
            ) : (
              <div className="print-wrapper">
                <table className="min-w-[600px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]">
                          <input
                            type="checkbox"
                            id="agreement"
                            className="hidden peer print-hidden"
                            onChange={handleSelectAll}
                            checked={
                              currentData.length > 0 &&
                              currentData.every((item) =>
                                selectedRows.includes(item.id)
                              )
                            }
                            disabled={loading}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor="agreement"
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                          ></label>
                        </div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Serial No
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Agent Code
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Agent Name
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Contact Number
                      </th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((agent, index) => (
                      <tr
                        key={agent.id}
                        className="border-b hover:bg-gray-50"
                      >
                        <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print">
                          <div className="relative left-3 -top-[3px]">
                            <input
                              type="checkbox"
                              id={`agent-${index}`}
                              className="hidden peer print-hidden"
                              checked={selectedRows.includes(agent.id)}
                              onChange={() => handleRowSelect(agent.id)}
                              disabled={loading}
                            />
                            <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                              <FaCheck className="text-slate-100" />
                            </span>
                            <label
                              htmlFor={`agent-${index}`}
                              className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                            ></label>
                          </div>
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          {agent.displayId}
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          {agent.agentCode}
                        </td>
                        <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          {agent.name}
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          {agent.contactNo}
                        </td>
                        <td className="p-3 text-right space-x-2 print-hidden-in-print">
                          <button
                            onClick={() => handleEdit(agent)}
                            className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                            disabled={loading}
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedRows([agent.id]);
                              setDeleteModalOpen(true);
                            }}
                            className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                            disabled={loading}
                            title="Delete"
                          >
                            <RiDeleteBin6Line />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {totalPages > 1 && (
            <div className="p-5 text-end print-hidden">
              <div className="inline-flex items-center justify-between w-full">
                <p className="text-sm text-gray-600">
                  Records: {currentPage * itemsPerPage - itemsPerPage + 1} to{" "}
                  {Math.min(
                    currentPage * itemsPerPage,
                    filteredAgents.length
                  )}{" "}
                  of {filteredAgents.length}
                </p>
                <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
                  <li>
                    <button
                      className="table-pagination-item disabled:opacity-50"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <MdKeyboardArrowLeft />
                    </button>
                  </li>
                  {totalPages <= 10
                    ? [...Array(totalPages)].map((_, i) => (
                        <li key={i}>
                          <button
                            className={`table-pagination-item ${
                              currentPage === i + 1
                                ? "bg-M-primary-color text-white"
                                : ""
                            }`}
                            onClick={() => handlePageChange(i + 1)}
                          >
                            {i + 1}
                          </button>
                        </li>
                      ))
                    : (() => {
                        const pages = [];
                        const firstPages = 5;
                        const lastPages = 3;

                        for (
                          let i = 1;
                          i <= Math.min(firstPages, totalPages);
                          i++
                        ) {
                          pages.push(
                            <li key={i}>
                              <button
                                className={`table-pagination-item ${
                                  currentPage === i
                                    ? "bg-M-primary-color text-white"
                                    : ""
                                }`}
                                onClick={() => handlePageChange(i)}
                              >
                                {i}
                              </button>
                            </li>
                          );
                        }

                        if (totalPages > firstPages + lastPages) {
                          pages.push(
                            <li key="ellipsis" className="table-pagination-item">
                              ...
                            </li>
                          );
                        }

                        for (
                          let i = Math.max(
                            firstPages + 1,
                            totalPages - lastPages + 1
                          );
                          i <= totalPages;
                          i++
                        ) {
                          pages.push(
                            <li key={i}>
                              <button
                                className={`table-pagination-item ${
                                  currentPage === i
                                    ? "bg-M-primary-color text-white"
                                    : ""
                                }`}
                                onClick={() => handlePageChange(i)}
                              >
                                {i}
                              </button>
                            </li>
                          );
                        }

                        return pages;
                      })()}
                  <li>
                    <button
                      className="table-pagination-item"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <MdKeyboardArrowRight />
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        ids={selectedRows}
        entityName="agent"
        title="Confirm Delete"
        message={
          Array.isArray(selectedRows) && selectedRows.length > 1
            ? `Do you really want to delete ${selectedRows.length} agent(s)?`
            : `Do you really want to delete this agent?`
        }
        confirmText="Delete"
        cancelText="Cancel"
      />
      <HospitalAgentModal
        isOpen={agentModalOpen}
        onClose={() => {
          setAgentModalOpen(false);
          setAgentToEdit(null);
        }}
        onSave={handleSaveAgent}
        agentToEdit={agentToEdit}
      />
    </div>
  );
};

export default HospitalAgentList;